import { useI18n, useMCAInfo, useProfile } from 'contexts/AuthContext'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import ContentHeader from '../ContentHeader'
import CareSelectButton from 'components/CareSelectButton/index'
import { Button } from 'antd'
import { useNavigate } from 'react-router-dom'
import { getBMI, isMobile } from 'utils/basic'
import _ from 'lodash'
import { logAmplitudeEvent } from 'utils/amplitudeInit'
import { ScreenKey } from '../util'
import { useMCA } from './useMCA'
import { useMetadataOptions } from 'hooks/careHooks'

const getDefaultWlcConditionValue = (bmi) => {
  if (bmi >= 30) return 'obesity'
  if (bmi >= 25) return 'overweight'
  return null
}

const EXCLUSIVE_WEIGHT_VALUES = ['obesity', 'overweight']

const WLCCondition = (props) => {
  const { i18n } = useI18n()
  const { sku, setCurrentKey } = props
  const { setMCAInfo, mcaInfo } = useMCAInfo()
  const [isInitialized, setIsInitialized] = useState(true)
  const { profile } = useProfile()
  const { getNextScreenKey } = useMCA({ sku })
  const { mcaWlcConditions } = useMetadataOptions()
  const navigate = useNavigate()

  const { wlcCondition = [] } = mcaInfo

  useEffect(() => {
    logAmplitudeEvent('Screen View: Onboarding - WLC Condition', {
      sku,
    })
  }, [sku])

  const bmi = useMemo(() => {
    const { weightLbs, heightFt, heightIn } = profile
    return getBMI(Number(weightLbs), Number(heightFt) * 12 + Number(heightIn))
  }, [profile])

  const isSelected = useCallback(
    (id) => {
      return wlcCondition.some((item) => item.id === id)
    },
    [wlcCondition]
  )

  const setSelected = useCallback(
    (item) => {
      const isExist = isSelected(item.id)
      if (isExist) {
        setMCAInfo({
          ...mcaInfo,
          wlcCondition: wlcCondition.filter((i) => i.id !== item.id),
        })
      } else if (item.value === 'none_of_above') {
        setMCAInfo({ ...mcaInfo, wlcCondition: [item] })
      } else {
        const newWlcCondition = wlcCondition.filter(
          (i) =>
            i.value !== 'none_of_above' &&
            !(
              EXCLUSIVE_WEIGHT_VALUES.includes(item.value) &&
              EXCLUSIVE_WEIGHT_VALUES.includes(i.value)
            )
        )
        setMCAInfo({
          ...mcaInfo,
          wlcCondition: [...newWlcCondition, item],
        })
      }
    },
    [isSelected, mcaInfo, setMCAInfo, wlcCondition]
  )

  useEffect(() => {
    const defaultValue = getDefaultWlcConditionValue(bmi)
    if (
      !isInitialized ||
      !_.isEmpty(wlcCondition) ||
      _.isEmpty(mcaWlcConditions) ||
      !defaultValue
    ) {
      return
    }

    const defaultWlcCondition = mcaWlcConditions.filter(
      (item) => item.value === defaultValue
    )
    if (_.isEmpty(defaultWlcCondition)) {
      return
    }

    setMCAInfo({ ...mcaInfo, wlcCondition: defaultWlcCondition })
    setIsInitialized(false)
  }, [bmi, mcaInfo, setMCAInfo, wlcCondition, isInitialized, mcaWlcConditions])

  const isNextButtonEnable = useMemo(
    () =>
      wlcCondition.length > 1 ||
      ['none_of_above', 'hypertension'].includes(wlcCondition[0]?.value),
    [wlcCondition]
  )

  const openNextScreen = useCallback(async () => {
    const nextScreenKey = await getNextScreenKey(ScreenKey.MCA_WLC_CONDITION)
    setCurrentKey(nextScreenKey)
  }, [setCurrentKey, getNextScreenKey])

  const onSubmit = () => {
    logAmplitudeEvent('Click: Onboarding - WLC Condition - Submit', {
      sku,
      selections: wlcCondition.map((item) => item.name),
    })

    openNextScreen()
  }

  const onBack = (e) => {
    navigate(-1)
  }

  return (
    <div className='info-sec'>
      <ContentHeader {...props} onBack={onBack} />
      <div className='info-content'>
        <div className='title-pns' style={{ marginBottom: '16px' }}>
          {i18n(
            'Which conditions have you had in the past 12 months? (check all that apply)'
          )}
        </div>
        <div className='subtitle' style={{ marginBottom: '32px' }}>
          {i18n(
            'To be eligible, you must have high blood pressure or at least 2 other conditions.'
          )}
        </div>
        {mcaWlcConditions.map((item, index) => (
          <div key={index}>
            <CareSelectButton
              key={index}
              {...{
                selected: isSelected(item.id),
                setSelected: () => {
                  setSelected(item)
                },
                text: item.name,
                className: 'm-b-12 center-align',
                textAlign: 'center',
              }}
            />
          </div>
        ))}
        <Button
          className='btn-primary full-width-btn'
          style={isMobile() ? { width: '100%' } : {}}
          onClick={onSubmit}
          disabled={!isNextButtonEnable}
        >
          {i18n('Next')}
        </Button>
      </div>
    </div>
  )
}

export default WLCCondition
